<template>
  <div class="apply-button-wrap">
    <button
      v-if="showApplyButton && !isDeadlinePassed"
      class="apply-button"
      @click="handleApplyClick"
    >
      <span>공모 지원하기</span>
      <img src="@/assets/images/icon/ico_pointer.svg" alt="" />
    </button>

    <ContestApplyModal
      :visible="showApplyModal"
      :contest-uuid="contestUuid"
      :form-fields="formFields"
      :preview-image-url="contestMeta.previewImageUrl"
      :title="contestMeta.title"
      :description="contestMeta.description"
      @close="showApplyModal = false"
    />
  </div>
</template>

<script setup>
import { ref, defineProps, onMounted, reactive } from "vue";
import ContestApplyModal from "@/views/contest/ContestApplyModal.vue";
import { apiClient } from "@/services/auth-header";
import { useAuthInfo } from "@/composables/useAuthInfo";
import { useModalStore } from "@/store/modalStore";
import { useRoute } from "vue-router";

const route = useRoute();
const isInternal = route.name === "InternalNewsDetail";

const modal = useModalStore();
const { isArtist } = useAuthInfo();
const showApplyModal = ref(false);
const showApplyButton = ref(false);
const formFields = ref([]);
const isContestActive = ref(false);
const isDeadlinePassed = ref(false);
const contestUuid = ref(null);

const props = defineProps({
  articleUuid: {
    type: String,
    required: true,
  },
});

const contestMeta = reactive({
  previewImageUrl: "",
  title: "",
  description: "",
});

const handleApplyClick = async () => {
  if (!isArtist.value) {
    alert(
      "공모전 신청은 프로필을 등록한 아티스트 회원만 가능합니다.\n일반 회원이시라면 [MY PAGE > 나의 정보]에서 아티스트 전환을 신청해주세요."
    );
    return;
  }
  const { active, alreadyApplied } = await checkApplicable();

  if (!active) {
    alert("현재는 공모전 신청 기간이 아닙니다.");
    return;
  }

  if (alreadyApplied) {
    modal.open({
      title: "이미 신청한 공모전입니다",
      message: "이 공모전에 이미 신청하셨습니다.\n다시 작성하시겠습니까?",
      confirmText: "다시 신청하기",
      cancelText: "닫기",
      onConfirm: () => {
        showApplyModal.value = true;
      },
    });
    return;
  }

  showApplyModal.value = true;
};

const checkApplicable = async () => {
  try {
    const { data } = await apiClient.get(
      `/v1/artists/contest-applicable/${contestUuid.value}`
    );
    return data;
  } catch (e) {
    console.error(e);
  }
};

const fetchContest = async (isInternal) => {
  try {
    const { data } = await apiClient.get(
      `/v1/contents/contests/by-article/${props.articleUuid}`,
      { params: { isInternal } }
    );
    // uuid가 존재하면 유효한 공모전
    if (data?.uuid) {
      contestUuid.value = data.uuid;
      contestMeta.previewImageUrl = data.previewImageUrl;
      contestMeta.title = data.title;
      contestMeta.description = data.description;
      isContestActive.value = data.isActive;
      isDeadlinePassed.value = data.isDeadlinePassed;
      formFields.value = JSON.parse(data.formSchemaJson || "[]");
      showApplyButton.value = true;

      if (isInternal) showApplyModal.value = true;
    }
  } catch (e) {
    console.warn("해당 뉴스와 연결된 공모전이 없습니다.");
  }
};

onMounted(async () => {
  await fetchContest(isInternal);
});
</script>

<style scoped>
.apply-button-wrap {
  margin-top: 25px;
}

@media (max-width: 768px) {
  .apply-button-wrap {
    margin: 10px 0;
  }
}

.apply-button {
  width: 168px;
  height: 43px;
  padding: 12px 20px;
  border-radius: 40px;
  border: none;
  color: #ffffff;
  background: #0900ff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  cursor: pointer;
  gap: 4px;
}

.apply-button span {
  display: flex;
  align-items: center;
}

.apply-button img {
  width: 17px;
  height: 17px;
  display: flex;
  align-items: center;
}

@media (max-width: 768px) {
  .apply-button {
    font-size: 14px;
    margin-top: 15px;
    width: 138px;
    height: 36px;
  }

  .apply-button span {
    display: flex;
    align-items: center;
    margin-top: 1px;
  }

  .apply-button img {
    width: 14px;
    height: 14px;
    display: flex;
    align-items: center;
  }
}

.apply-button:active {
  transform: scale(0.97);
}
</style>
